import { useId, useState } from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import {
  centsToDisplay,
  useStorefrontPublicCategoryPickerOptions,
  useStorefrontPublicProductPickerOptions,
  type StorefrontPublicCategoryPickerOption,
  type SupabaseClient,
} from '@dukanti/shared';
import { useTranslation } from '@dukanti/shared/i18n';
import { Button } from '@/components/ui/button';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { cn } from '@/lib/utils';
import {
  PickerOption,
  PickerPrompt,
  PickerSearch,
  PickerSelection,
  PickerStatus,
  type PickerPromptTone,
} from './picker-parts';

export interface ProductDestinationPickerProps {
  readonly client: SupabaseClient;
  readonly supabaseUrl: string;
  readonly storeId: string;
  /** The stored product id ('' = nothing picked yet). */
  readonly selectedId: string;
  /** Its name from the store's own list (PD10); never offered as an option. */
  readonly selectedLabel: string | null;
  readonly unavailable: boolean;
  /** How the "choose one" prompt reads while nothing is picked. */
  readonly promptTone?: PickerPromptTone;
  readonly onSelect: (productId: string) => void;
  readonly testIdPrefix: string;
}

function formatPrice(cents: number, lang: string): string {
  return new Intl.NumberFormat(lang, {
    style: 'currency',
    currency: 'ILS',
    maximumFractionDigits: 2,
  }).format(centsToDisplay(cents));
}

/**
 * A searchable list of the products a shopper can open, from the f075
 * picker RPC, with the ads picker's category filter. Search runs on the
 * server 250 ms after typing stops.
 */
export function ProductDestinationPicker({
  client,
  supabaseUrl,
  storeId,
  selectedId,
  selectedLabel,
  unavailable,
  promptTone = 'neutral',
  onSelect,
  testIdPrefix,
}: ProductDestinationPickerProps): React.JSX.Element {
  const { t, lang } = useTranslation();
  const id = useId();
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [category, setCategory] = useState<StorefrontPublicCategoryPickerOption | null>(null);
  const debounced = useDebouncedValue(search, 250);
  const query = useStorefrontPublicProductPickerOptions(client, {
    storeId,
    search: debounced,
    categoryId: category?.id ?? null,
    supabaseUrl,
  });
  const categories = useStorefrontPublicCategoryPickerOptions(client, {
    storeId: filterOpen ? storeId : null,
    search: '',
    supabaseUrl,
  });
  const rows = query.data ?? [];
  const filtering = debounced.trim() !== '' || category !== null;
  const label =
    selectedLabel ??
    rows.find((row) => row.id === selectedId)?.name ??
    t('admin.destination.selectedUnknown');
  const panelId = `${id}-filter`;

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-product-picker`}>
      {selectedId !== '' ? (
        <PickerSelection
          text={t('admin.destination.selectedProduct', {
            name: label,
            interpolation: { escapeValue: false },
          })}
          unavailable={unavailable}
          testIdPrefix={testIdPrefix}
        />
      ) : null}
      <PickerSearch
        id={`${id}-search`}
        label={t('admin.destination.productSearchLabel')}
        placeholder={t('admin.destination.productSearchPlaceholder')}
        value={search}
        onChange={setSearch}
        testId={`${testIdPrefix}-product-search`}
      />

      <div className="space-y-2">
        <Button
          type="button"
          variant="outline"
          aria-expanded={filterOpen}
          aria-controls={panelId}
          className="min-h-11 w-full justify-between text-start"
          data-testid={`${testIdPrefix}-product-filter`}
          onClick={() => setFilterOpen((open) => !open)}
        >
          <span className="flex min-w-0 items-center gap-2">
            <SlidersHorizontal className="size-4 shrink-0" aria-hidden />
            <span className="min-w-0 truncate">
              {t('admin.destination.productCategoryFilterButton', {
                name: category?.name ?? t('admin.destination.productCategoryFilterAll'),
                interpolation: { escapeValue: false },
              })}
            </span>
          </span>
          <ChevronDown
            className={cn(
              'text-muted-foreground size-4 shrink-0 transition-transform',
              filterOpen && 'rotate-180',
            )}
            aria-hidden
          />
        </Button>
        {filterOpen ? (
          <div
            id={panelId}
            role="group"
            aria-label={t('admin.destination.productCategoryFilterSearchLabel')}
            className="border-border bg-card max-h-52 space-y-1 overflow-y-auto rounded-lg border p-2"
          >
            <Button
              type="button"
              variant={category === null ? 'default' : 'ghost'}
              size="sm"
              aria-pressed={category === null}
              className="min-h-11 w-full justify-start"
              onClick={() => {
                setCategory(null);
                setFilterOpen(false);
              }}
            >
              {t('admin.destination.productCategoryFilterAll')}
            </Button>
            {(categories.data ?? []).map((option) => (
              <Button
                key={option.id}
                type="button"
                variant={category?.id === option.id ? 'default' : 'ghost'}
                size="sm"
                aria-pressed={category?.id === option.id}
                className="min-h-11 w-full justify-between gap-3"
                data-testid={`${testIdPrefix}-product-filter-${option.id}`}
                onClick={() => {
                  setCategory(option);
                  setFilterOpen(false);
                }}
              >
                <span className="min-w-0 truncate">{option.name}</span>
                <span className="text-xs opacity-70">{option.product_count}</span>
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      <PickerStatus
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={rows.length === 0}
        loadingKey="admin.destination.loadingProducts"
        retryKey="admin.destination.retryProducts"
        emptyKey={filtering ? 'admin.destination.productEmpty' : 'admin.destination.noProducts'}
        onRetry={() => void query.refetch()}
        testIdPrefix={`${testIdPrefix}-product`}
      />
      {selectedId === '' && rows.length > 0 && !query.isError && !query.isLoading ? (
        <PickerPrompt
          text={t('admin.destination.pickProduct')}
          tone={promptTone}
          testId={`${testIdPrefix}-product-prompt`}
        />
      ) : null}
      {rows.length > 0 && !query.isError ? (
        <div
          className="border-border divide-border max-h-80 divide-y overflow-y-auto rounded-lg border"
          aria-label={t('admin.destination.pickProduct')}
          role="group"
        >
          {rows.map((product) => (
            <PickerOption
              key={product.id}
              selected={product.id === selectedId}
              onSelect={() => {
                if (product.id !== selectedId) onSelect(product.id);
              }}
              testId={`${testIdPrefix}-product-option-${product.id}`}
            >
              <span className="flex w-full min-w-0 items-start justify-between gap-3">
                <span className="min-w-0">
                  <span className="text-foreground block truncate font-medium">{product.name}</span>
                  <span className="text-muted-foreground block truncate text-xs">
                    {product.categoryName}
                  </span>
                </span>
                <span className="text-foreground shrink-0 text-xs font-medium" dir="ltr">
                  {formatPrice(product.sale_price_cents ?? product.price_cents, lang)}
                </span>
              </span>
              <span
                className={cn(
                  'mt-1.5 inline-flex rounded-full border px-2 py-0.5 text-xs font-medium',
                  product.isOutOfStock
                    ? 'border-input bg-admin-surface-muted text-foreground/75'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/15 dark:text-emerald-200',
                )}
              >
                {t(
                  product.isOutOfStock
                    ? 'admin.destination.outOfStock'
                    : 'admin.destination.inStock',
                )}
              </span>
            </PickerOption>
          ))}
        </div>
      ) : null}
    </div>
  );
}
